import { useEffect, useMemo } from "react";
import type { CSSProperties } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import TrackWeekWeatherCard from "../components/TrackWeekWeatherCard";
import { useAuthStore } from "../state/authStore";
import { useRunStore } from "../state/runStore";
import { useTeamStore } from "../state/teamStore";
import { formatDateRange } from "../utils/scheduleWeather";

const TOOL_TABS = [
  { path: "overview", label: "Overview" },
  { path: "drivers", label: "Drivers" },
  { path: "qualifying", label: "Qualifying" },
  { path: "master", label: "Master" },
  { path: "head-to-head", label: "Head-to-Head" },
  { path: "fuel", label: "Fuel" },
  { path: "stints", label: "Stints" },
  { path: "handling", label: "Handling" },
  { path: "pit", label: "Pit" },
  { path: "checklist", label: "Checklist" },
  { path: "trends", label: "Trends" },
] as const;

export default function WeekWorkspace() {
  const { user } = useAuthStore();
  const { teamId, seriesSlug, trackWeekId } = useParams<{
    teamId: string;
    seriesSlug: string;
    trackWeekId: string;
  }>();
  const location = useLocation();
  const { activeTeamId, setActiveTeam, activeTeam } = useTeamStore();
  const { selectedTrackWeekId, setTrackWeek, trackWeeks, fetchTrackWeeks } = useRunStore();
  const displayUnits = user?.preferences?.units || "standard";

  useEffect(() => {
    if (teamId && teamId !== activeTeamId) {
      setActiveTeam(teamId);
    }
  }, [teamId, activeTeamId, setActiveTeam]);

  useEffect(() => {
    if (trackWeekId && trackWeekId !== selectedTrackWeekId) {
      setTrackWeek(trackWeekId);
    }
  }, [trackWeekId, selectedTrackWeekId, setTrackWeek]);

  useEffect(() => {
    if (teamId) {
      void fetchTrackWeeks(teamId);
    }
  }, [teamId, fetchTrackWeeks]);

  const currentWeek = useMemo(
    () => trackWeeks.find((week) => week.id === trackWeekId) || null,
    [trackWeeks, trackWeekId],
  );

  if (!teamId || !trackWeekId) {
    return <Navigate to="/teams" replace />;
  }

  const basePath = `/teams/${teamId}/series/${seriesSlug}/weeks/${trackWeekId}`;
  const activeTool = location.pathname.replace(basePath, "").replace(/^\//, "") || "overview";
  const weekLabel = currentWeek?.displayWeekNumber ?? currentWeek?.weekNumber;
  const dateLabel = formatDateRange(currentWeek?.startsAt, currentWeek?.endsAt);

  return (
    <div className="page page--wide">
      <Link to={`/teams/${teamId}/series/${seriesSlug}`} className="back-link">
        &larr; Back to series
      </Link>

      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">
              {[
                currentWeek?.seriesName || "Series",
                weekLabel != null ? `Week ${weekLabel}` : null,
                dateLabel || null,
              ].filter(Boolean).join("  ·  ")}
            </span>
          </div>
          <div className="editorial-meta-right" style={styles.headerMeta}>
            {activeTeam && <span className="pill pill--accent">{activeTeam.name}</span>}
            {currentWeek?.carName && <span className="pill">{currentWeek.carName}</span>}
          </div>
        </div>
        <h1 className="editorial-title">{currentWeek?.trackName || "Week"}</h1>
      </header>

      <TrackWeekWeatherCard week={currentWeek} units={displayUnits} />

      <div className="tabs" style={{ margin: "1.25rem 0 1.25rem" }}>
        {TOOL_TABS.map(({ path, label }) => (
          <NavLink
            key={path}
            to={`${basePath}/${path}`}
            end
            className={`tab ${activeTool === path ? "active" : ""}`}
          >
            {label}
          </NavLink>
        ))}
      </div>

      <div className="fade-in">
        <Outlet />
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  headerMeta: {
    display: "flex",
    alignItems: "center",
    gap: "0.65rem",
    flexWrap: "wrap",
  },
};
